import { useFormikContext } from 'formik';

import type { ResultOf } from '@graphql-typed-document-node/core';

import Stack from '@mui/material/Stack';

import { ensureDefined, invariant } from '@e-socna/utils';
import { activeMembershipOf } from '@e-socna/folder-actors';
import {
  getFragmentData,
  graphql,
  ThirdPartyTypeEnum,
} from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { useThirdPartiesQuery } from '../../../../components/ThirdParties/hooks/useThirdPartiesQuery.hook';
import { NoData } from '../../../../components/Illustrations/NoData';
import { Loading } from '../../../../components/Loading';
import { ThirdPartiesCmp_Person_Fragment } from '../../../../components/ThirdParties/fragments/person.fragment';
import { ActorCardCmp_OrganizationMembership_Fragment } from '../../../../components/Actors/Card/fragments/organization-membership.fragment';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { RepresentativeDetails } from './RepresentativeDetails';

export const ActorForm_RepresentativesDetailsStep_ActorOrganization_Fragment =
  graphql(`
    fragment ActorForm_RepresentativesDetailsStep_ActorOrganization_Fragment on ActorOrganization {
      isInternal
      representatives {
        _id
        contextualRoleIds
        index
        typeIds
        personId
      }
    }
  `);

export type ActorForm_RepresentativesDetailsStep_ActorOrganization = ResultOf<
  typeof ActorForm_RepresentativesDetailsStep_ActorOrganization_Fragment
>;

export type ActorForm_RepresentativesDetailsStep_Representative =
  ActorForm_RepresentativesDetailsStep_ActorOrganization['representatives'][number];

interface Props {
  readonly memberships:
    | readonly FragmentType<
        typeof ActorCardCmp_OrganizationMembership_Fragment
      >[]
    | undefined;
}

export const RepresentativesDetails = ({
  memberships: pMemberships,
}: Readonly<Props>) => {
  const memberships = getFragmentData(
    ActorCardCmp_OrganizationMembership_Fragment,
    pMemberships,
  );

  const {
    values: { isInternal, representatives },
  } =
    useFormikContext<ActorForm_RepresentativesDetailsStep_ActorOrganization>();

  const { genericStatusesCodeMap } = useReferencesContext();
  const { data, isSuccess } = useThirdPartiesQuery({
    isInternal,
    kind: ThirdPartyTypeEnum.Person,
  });

  if (!representatives.length) {
    return <NoData />;
  }

  if (!isSuccess) {
    return <Loading />;
  }

  invariant(
    !!data && 'findSomePeople' in data,
    'findSomePeople must be defined',
  );

  const people = getFragmentData(
    ThirdPartiesCmp_Person_Fragment,
    data.findSomePeople,
  );

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {representatives.map((representative, index) => {
        const { person } = ensureDefined({
          person: people.find(
            (person) => person._id === representative.personId,
          ),
        });

        const membership = activeMembershipOf(
          memberships ?? [],
          person._id,
          genericStatusesCodeMap.Enabled._id,
        );

        return (
          <RepresentativeDetails
            index={index}
            isInternal={isInternal}
            key={representative.personId}
            membership={membership}
            person={person}
          />
        );
      })}
    </Stack>
  );
};
